import { useEffect, useRef, useState } from "react";
import { Button } from "@component/button";
import { Input } from "@component/input";
import { Label } from "@component/label";
import { Checkbox } from "@component/checkbox";
import { Switch } from "@component/switch";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@component/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@component/select";
import { Popover, PopoverContent, PopoverTrigger } from "@component/popover";
import {
    BotIcon,
    AlertTriangleIcon,
    CheckCircle2Icon,
    ChevronDownIcon,
    ChevronUpIcon,
    ClipboardListIcon,
    FileTextIcon,
    FilterIcon,
    FlagIcon,
    GripVerticalIcon,
    HandshakeIcon,
    LinkIcon,
    LockIcon,
    MessageCircleIcon,
    EyeIcon,
    Loader2Icon,
    PhoneIcon,
    PlayIcon,
    PlusIcon,
    RefreshCcw,
    Trash2Icon,
    XCircleIcon,
    XIcon,
} from "lucide-react";
import { cn } from "@util/tailwind";
import { useSnackbar } from "@hook/use-snackbar";
import { catchErrorLog } from "@util/sentry";
import { getHrJobListings } from "@service/job";
import {
    createHiringFlowTemplate,
    deleteHiringFlowTemplate,
    getHiringFlowTemplates,
    HiringFlowTemplate,
    previewFlowChat,
    FlowPreviewMessage,
} from "@service/hiring-flow";
import { getAssessmentQuestions, AssessmentQuestionOption } from "@service/assessment";
import { Assessment, FormAssessment, InterviewAssessment } from "@type/assessments";
import {
    checkHiringFlow,
    createStep,
    DeadlineGate,
    gatesPatch,
    HiringFlowGate,
    stepGates,
    MAX_CUSTOM_MESSAGE_LENGTH,
    DOCUMENT_CUSTOM,
    DOCUMENT_TEMPLATE,
    documentLabel,
    duplicateAssessmentGroups,
    HiringFlow,
    HiringFlowStep,
    HiringFlowStepType,
    KnockoutOperator,
    MAX_FLOW_STEPS,
    STEP_TYPE_DESCRIPTIONS,
    STEP_TYPE_LABELS,
    KNOCKOUT_OPERATOR_LABELS,
    ELIGIBILITY_FIELD_LABELS,
} from "@type/hiring-flow";
import { JobCreateRequest } from "@service/employer";
import { availabilityOptions, employmentStatusOptions } from "@constant/app";
import { Textarea } from "@component/textarea";
import { Tooltip } from "@component/tooltip";

const STEP_TYPE_ICONS: Record<HiringFlowStepType, React.ComponentType<{ className?: string }>> = {
    form: ClipboardListIcon,
    ai_interview: BotIcon,
    eligibility_check: CheckCircle2Icon,
    external_link: LinkIcon,
    documents: FileTextIcon,
    human_interview: HandshakeIcon,
    custom_message: MessageCircleIcon,
};

const CATALOG: HiringFlowStepType[] = [
    "form",
    "ai_interview",
    "eligibility_check",
    "external_link",
    "documents",
    // human_interview retired (#3255, BE #3993) — existing flows keep rendering
    // and running, but the step can no longer be added.
    "custom_message",
];

// Wording lives in @type/hiring-flow so the Version History describes a
// condition exactly the way the builder does.
const KNOCKOUT_OPERATORS: { value: KnockoutOperator; label: string }[] =
    (Object.entries(KNOCKOUT_OPERATOR_LABELS) as [KnockoutOperator, string][])
        .map(([value, label]) => ({ value, label }));

const ELIGIBILITY_FIELDS: { key: keyof NonNullable<JobCreateRequest["work_authorization_metadata"]>; label: string }[] =
    (Object.entries(ELIGIBILITY_FIELD_LABELS) as [keyof NonNullable<JobCreateRequest["work_authorization_metadata"]>, string][])
        .map(([key, label]) => ({ key, label }));

// #3236: canonical ANSWER options per eligibility field — mirrors the candidate
// survey (SWEExperienceSurvey → employmentStatusOptions / availabilityOptions /
// its inline SelectItems) so the knockout VALUE offers exactly the choices the
// candidate can pick, storing the same canonical keys the answers store. Salary
// fields are free numbers ("number" → a number input, not a dropdown).
const YES_NO_DISCUSS_OPTIONS = [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
    { value: "open-to-discussion", label: "Open to discussion" },
];
const ELIGIBILITY_VALUE_OPTIONS: Record<string, { value: string; label: string }[] | "number"> = {
    collect_employment_status: Object.entries(employmentStatusOptions).map(([value, label]) => ({ value, label })),
    collect_expected_salary: "number",
    collect_start_availability: availabilityOptions,
    collect_current_salary: "number",
    collect_relocation_willingness: YES_NO_DISCUSS_OPTIONS,
    collect_weekend_shift_willingness: YES_NO_DISCUSS_OPTIONS,
    // getWorkAuthorizationOptions(country) interpolates the job's country into the
    // candidate-facing labels; the builder shows country-less HR labels, same keys.
    collect_work_authorization: [
        { value: "citizen", label: "Citizen" },
        { value: "permanent_resident", label: "Permanent resident" },
        { value: "work_visa", label: "Valid work visa or permit" },
        { value: "needs_visa", label: "Needs a work visa" },
    ],
    // Normalized ladder (#3085) — ordered; "is below Diploma" compares on it.
    collect_education_level: [
        { value: "below_secondary", label: "Below secondary" },
        { value: "secondary", label: "Secondary (SPM · GCSE / O-Levels)" },
        { value: "post_secondary", label: "Post-secondary (STPM · A-Levels)" },
        { value: "diploma", label: "Diploma" },
        { value: "bachelor", label: "Bachelor's degree" },
        { value: "postgraduate", label: "Postgraduate" },
    ],
};

interface Props {
    flow: HiringFlow;
    onChange: (flow: HiringFlow) => void;
    assessments: Assessment[];
    mayaEnabled: boolean;
    /** Company chat-native rollout flag (#3090) — gates the chat-native toggle + chat interview mode. */
    chatNativeEnabled?: boolean;
    disabled?: boolean;
    /** Current job id (edit mode) — excluded from the reroute suggest-job picker. */
    currentJobId?: string;
    workAuthMetadata: JobCreateRequest["work_authorization_metadata"];
    /** #3257: real values for the Custom Message preview's {{jobTitle}} / {{companyName}}. */
    previewJobTitle?: string;
    previewCompanyName?: string;
    /** Company WhatsApp rollout flag — with it OFF the BE coerces whatsapp_first
     *  to web, so custom_message + the WA preview are gated on it too (#3257). */
    whatsappEnabled?: boolean;
    /** Extra context for the BE flow-chat preview ({country} wording, wa_call). */
    previewLocationMetadata?: import("@service/employer").LocationMetadata | null;
    previewWaCallEnabled?: boolean;
    onWorkAuthChange: (key: keyof NonNullable<JobCreateRequest["work_authorization_metadata"]>, value: boolean) => void;
    onRefreshAssessments: () => void;
}

/** Node selection: the entry/start node or a step id. */
type NodeSelection = "entry" | string | null;

/**
 * Hiring Flow builder — Create Job wizard, Step 2 (#3070), flow-chart canvas
 * presentation (#3130). Composes the per-job `hiring_flow` (mockup §2 / BE
 * #3641) exactly as before — ordered steps from the curated 6-type catalog,
 * max 1 gate per step (flag & pause only), per-step advance (auto/review), and
 * the entry/channel job settings — but renders it as a top→down chart: a start
 * node, step nodes joined by connectors with inline "+" insertion, gates drawn
 * as labelled pass/fail branches, and a side panel to configure the selected
 * node. The emitted `HiringFlow` object (and therefore the job payload) is
 * unchanged; this is presentation only. Serialized by the parent form.
 */
// Deadline `due` grammar (BE #3641 / seed data): "same_day_HH:MM" and the
// relative "+Nh" are the confirmed forms; anything else (ISO, other tokens) is
// preserved verbatim via the Custom mode, so this friendlier editor never alters
// the stored value / payload. Reminders are offsets BEFORE the deadline ("-Nh").
const DUE_SAME_DAY_RE = /^same_day_(\d{1,2}):(\d{2})$/;
const DUE_IN_HOURS_RE = /^\+(\d+)h$/;

const REMINDER_PRESETS: { value: string; label: string }[] = [
    { value: "-1h", label: "1h before" },
    { value: "-2h", label: "2h before" },
    { value: "-4h", label: "4h before" },
    { value: "-8h", label: "8h before" },
    { value: "-12h", label: "12h before" },
    { value: "-24h", label: "1 day before" },
];

/**
 * Friendly editor for a Deadline gate (#3130 follow-up). Replaces the raw
 * "same_day_17:00" / "-4h,-1h" text inputs with a mode-select + time/number
 * picker for the due, and toggleable preset chips (+ a custom escape hatch) for
 * reminders — while emitting the exact same string shapes the BE expects.
 */
function DeadlineGateFields({
    gate,
    disabled,
    onChange,
}: {
    gate: DeadlineGate;
    disabled?: boolean;
    onChange: (gate: DeadlineGate) => void;
}) {
    const due = gate.due || "";
    const sameDayMatch = due.match(DUE_SAME_DAY_RE);
    const inHoursMatch = due.match(DUE_IN_HOURS_RE);
    const dueMode: "same_day" | "in_hours" | "custom" =
        sameDayMatch ? "same_day" : inHoursMatch ? "in_hours" : "custom";
    // HH:MM for <input type="time"> (pad the hour so "9:00" → "09:00").
    const sameDayTime = sameDayMatch ? `${sameDayMatch[1].padStart(2, "0")}:${sameDayMatch[2]}` : "17:00";
    const inHoursValue = inHoursMatch ? inHoursMatch[1] : "48";

    const setDue = (value: string) => onChange({ ...gate, due: value });

    const reminders = gate.reminders || [];
    const toggleReminder = (value: string) =>
        onChange({
            ...gate,
            reminders: reminders.includes(value) ? reminders.filter((r) => r !== value) : [...reminders, value],
        });
    const removeReminder = (value: string) => onChange({ ...gate, reminders: reminders.filter((r) => r !== value) });
    const customReminders = reminders.filter((r) => !REMINDER_PRESETS.some((p) => p.value === r));
    const [customReminderDraft, setCustomReminderDraft] = useState("");
    const addCustomReminder = () => {
        const v = customReminderDraft.trim();
        if (!v || reminders.includes(v)) { setCustomReminderDraft(""); return; }
        onChange({ ...gate, reminders: [...reminders, v] });
        setCustomReminderDraft("");
    };

    return (
        <div className="space-y-3">
            {/* Due */}
            <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-amber-800">Finish by</Label>
                <div className="flex flex-wrap items-center gap-2">
                    <Select
                        value={dueMode}
                        onValueChange={(mode) => {
                            if (mode === "same_day") setDue(`same_day_${sameDayTime}`);
                            else if (mode === "in_hours") setDue(`+${inHoursValue}h`);
                            // custom: keep the current string as an editable raw value
                        }}
                        disabled={disabled}
                    >
                        <SelectTrigger className="w-[150px] text-sm flex-shrink-0 bg-white dark:bg-gray-900"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="same_day">Same day, by…</SelectItem>
                            <SelectItem value="in_hours">Within hours…</SelectItem>
                            <SelectItem value="custom">Custom…</SelectItem>
                        </SelectContent>
                    </Select>
                    {dueMode === "same_day" && (
                        <input
                            type="time"
                            value={sameDayTime}
                            onChange={(e) => setDue(`same_day_${e.target.value || "17:00"}`)}
                            disabled={disabled}
                            className="h-9 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 text-sm text-gray-900 dark:text-gray-100 disabled:opacity-60"
                        />
                    )}
                    {dueMode === "in_hours" && (
                        <div className="inline-flex items-center gap-1.5">
                            <Input
                                type="number"
                                min={1}
                                className="w-[80px]"
                                value={inHoursValue}
                                onChange={(e) => setDue(`+${Math.max(1, parseInt(e.target.value || "1", 10) || 1)}h`)}
                                disabled={disabled}
                            />
                            <span className="text-sm text-gray-500">hours after start</span>
                        </div>
                    )}
                    {dueMode === "custom" && (
                        <Input
                            className="flex-1 min-w-[150px] font-mono text-xs"
                            placeholder="same_day_17:00 · +48h · ISO date"
                            value={due}
                            onChange={(e) => setDue(e.target.value)}
                            disabled={disabled}
                        />
                    )}
                </div>
            </div>

            {/* Reminders */}
            <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-amber-800">Remind before the deadline</Label>
                <div className="flex flex-wrap gap-1.5">
                    {REMINDER_PRESETS.map((preset) => {
                        const active = reminders.includes(preset.value);
                        return (
                            <button
                                key={preset.value}
                                type="button"
                                disabled={disabled}
                                onClick={() => toggleReminder(preset.value)}
                                className={cn(
                                    "rounded-full px-2.5 py-1 text-xs font-medium border transition-colors",
                                    active
                                        ? "bg-blue-600 border-blue-600 text-white"
                                        : "bg-white dark:bg-gray-900 border-amber-200 dark:border-amber-900/60 text-amber-800 dark:text-amber-300 hover:border-blue-400",
                                    disabled && "opacity-60 pointer-events-none",
                                )}
                            >
                                {preset.label}
                            </button>
                        );
                    })}
                    {/* Non-preset reminders kept as removable chips (back-compat, e.g. "-30m"). */}
                    {customReminders.map((r) => (
                        <span key={r} className="inline-flex items-center gap-1 rounded-full bg-blue-600 px-2.5 py-1 text-xs font-medium text-white">
                            {r}
                            <button type="button" disabled={disabled} onClick={() => removeReminder(r)} aria-label={`Remove ${r}`} className="hover:opacity-80 disabled:opacity-60">
                                <XIcon className="w-3 h-3" />
                            </button>
                        </span>
                    ))}
                </div>
                <div className="flex items-center gap-1.5">
                    <Input
                        className="w-[150px] h-8 text-xs"
                        placeholder="Custom (e.g. -30m)"
                        value={customReminderDraft}
                        onChange={(e) => setCustomReminderDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomReminder(); } }}
                        disabled={disabled}
                    />
                    <Button type="button" variant="outline" size="sm" className="h-8" disabled={disabled || !customReminderDraft.trim()} onClick={addCustomReminder}>
                        Add
                    </Button>
                </div>
                {reminders.length === 0 && (
                    <p className="text-[11px] text-amber-700/80">No reminders — the candidate is only notified when the step starts.</p>
                )}
            </div>
        </div>
    );
}

export default function HiringFlowBuilder(props: Props) {
    const { flow, onChange, disabled } = props;
    const snackbar = useSnackbar();
    const [dragIndex, setDragIndex] = useState<number | null>(null);

    // Canvas state (#3130) — which node the config panel edits, and which
    // connector's "+" menu is open (index = insertion position in steps[]).
    const [selectedId, setSelectedId] = useState<NodeSelection>(null);
    const [addMenuIndex, setAddMenuIndex] = useState<number | null>(null);

    // Templates (#3071) — system rows are locked 🔒; company rows are deletable.
    // When the endpoint isn't reachable the picker row is hidden (the builder
    // itself stays fully usable with the pre-seeded flow).
    const [templates, setTemplates] = useState<HiringFlowTemplate[]>([]);
    const [templatesAvailable, setTemplatesAvailable] = useState<boolean>(false);
    const [isSaveDialogOpen, setSaveDialogOpen] = useState<boolean>(false);
    const [templateName, setTemplateName] = useState<string>("");
    const [templateNameError, setTemplateNameError] = useState<boolean>(false);
    const [isSavingTemplate, setSavingTemplate] = useState<boolean>(false);

    const loadTemplates = () => {
        getHiringFlowTemplates()
            .then((rows) => {
                setTemplates(rows);
                setTemplatesAvailable(true);
            })
            .catch((err) => {
                catchErrorLog(`Failed to load hiring-flow templates: ${err}`);
                setTemplatesAvailable(false);
            });
    };

    useEffect(() => {
        loadTemplates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reroute suggest-job picker (#3086, BE #3662) — loaded lazily the first
    // time a knockout condition exists, so the wizard doesn't pay for the jobs
    // list otherwise. Published jobs only; the current job is excluded.
    const [suggestableJobs, setSuggestableJobs] = useState<{ id: string; job_title: string }[]>([]);
    const [suggestJobsLoaded, setSuggestJobsLoaded] = useState<boolean>(false);
    const hasKnockoutGate = flow.steps.some((s) => stepGates(s).some((g) => g.type === "answer_knockout"));
    useEffect(() => {
        if (!hasKnockoutGate || suggestJobsLoaded) return;
        setSuggestJobsLoaded(true);
        getHrJobListings({ page: 1, limit: 100, status: "Published" })
            .then((resp) => {
                const rows = (resp?.data?.data || []) as { id: string; job_title: string }[];
                setSuggestableJobs(rows.filter((j) => j.id && j.id !== props.currentJobId));
            })
            .catch((err) => {
                catchErrorLog(`Failed to load jobs for the reroute picker: ${err}`);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasKnockoutGate, suggestJobsLoaded]);

    // The picker's selection is provenance-matched: the template whose name is
    // stamped on the flow. "ourteam_default" matches the seeded ourteam Default row.
    const selectedTemplate = templates.find(
        (t) => t.name === flow.template || (flow.template === "ourteam_default" && t.is_system && t.name === "ourteam Default"),
    );

    const applyTemplate = (tpl: HiringFlowTemplate) => {
        const rawSteps = JSON.parse(JSON.stringify(tpl.steps || [])) as HiringFlowStep[];
        // #3161: restore the eligibility_check screening-field selection. Those fields
        // live in the job's work_authorization_metadata (not on the step); the template
        // carries them on the step as `fields` (BE #3813). Only re-apply when the
        // template actually carries `fields`, so older templates / pre-BE saves leave
        // the current selection untouched.
        const eligWithFields = rawSteps.find((s) => s.type === "eligibility_check" && Array.isArray(s.fields));
        if (eligWithFields) {
            const selected = new Set(eligWithFields.fields || []);
            ELIGIBILITY_FIELDS.forEach((f) => props.onWorkAuthChange(f.key, selected.has(f.key)));
        }
        // Strip `fields` so the live flow (and the job payload) never carries it — the
        // selection stays in work_authorization_metadata.
        const steps = rawSteps.map(({ fields: _fields, ...s }) => s);
        onChange({
            version: flow.version,
            template: tpl.name,
            channel: (tpl.channel || "web") as HiringFlow["channel"],
            entry: tpl.entry || { auto_start: false, cv_required_at_apply: true },
            steps,
        });
        setSelectedId(null);
    };

    const saveTemplate = () => {
        if (!templateName.trim()) {
            setTemplateNameError(true);
            return;
        }
        setSavingTemplate(true);
        // #3161: carry the eligibility_check screening-field selection (which lives in
        // work_authorization_metadata, not on the step) in the template as `fields`, so
        // loading it restores the checkboxes (BE #3813 preserves it).
        const stepsForTemplate = flow.steps.map((s) =>
            s.type === "eligibility_check"
                ? { ...s, fields: ELIGIBILITY_FIELDS.filter((f) => props.workAuthMetadata?.[f.key]).map((f) => f.key) }
                : s
        );
        createHiringFlowTemplate({
            name: templateName.trim(),
            steps: stepsForTemplate,
            channel: flow.channel,
            entry: flow.entry,
        })
            .then((created) => {
                snackbar.showSuccess(`Template "${created?.name || templateName.trim()}" saved.`);
                setSaveDialogOpen(false);
                setTemplateName("");
                loadTemplates();
                if (created?.name) onChange({ ...flow, template: created.name });
            })
            .catch(() => {
                snackbar.showError("Failed to save template. Please try again.");
            })
            .finally(() => setSavingTemplate(false));
    };

    const deleteSelectedTemplate = () => {
        if (!selectedTemplate || selectedTemplate.is_system) return;
        deleteHiringFlowTemplate(selectedTemplate.id)
            .then(() => {
                snackbar.showSuccess(`Template "${selectedTemplate.name}" deleted.`);
                onChange({ ...flow, template: "custom" });
                loadTemplates();
            })
            .catch(() => {
                snackbar.showError("Failed to delete template. System templates cannot be deleted.");
            });
    };

    const interviewAssessments = props.assessments.filter((a) => a.type === "Interview") as InterviewAssessment[];
    const formAssessments = props.assessments.filter((a) => a.type === "Form") as FormAssessment[];

    const checks = checkHiringFlow(flow, { waCallAvailable: props.mayaEnabled, companyWhatsappEnabled: props.whatsappEnabled });
    const failing = checks.filter((c) => !c.ok);
    // #3174: assessment_ids used by 2+ Form/AI-Interview steps → warn on those nodes.
    const dupAssessmentIds = new Set(duplicateAssessmentGroups(flow.steps).keys());

    const updateSteps = (steps: HiringFlowStep[]) => onChange({ ...flow, steps });

    const updateStep = (index: number, patch: Partial<HiringFlowStep>) => {
        updateSteps(flow.steps.map((s, i) => (i === index ? { ...s, ...patch } : s)));
    };

    /** Insert a new step at a specific position (the canvas "+" connectors). */
    const addStepAt = (type: HiringFlowStepType, at: number) => {
        if (flow.steps.length >= MAX_FLOW_STEPS) return;
        const step = createStep(type, flow.steps);
        const steps = [...flow.steps];
        steps.splice(Math.max(0, Math.min(at, steps.length)), 0, step);
        updateSteps(steps);
        setSelectedId(step.id);
        setAddMenuIndex(null);
    };

    const removeStep = (index: number) => {
        const removed = flow.steps[index];
        updateSteps(flow.steps.filter((_, i) => i !== index));
        if (removed && selectedId === removed.id) setSelectedId(null);
    };

    const moveStep = (from: number, to: number) => {
        if (to < 0 || to >= flow.steps.length) return;
        const steps = [...flow.steps];
        const [moved] = steps.splice(from, 1);
        steps.splice(to, 0, moved);
        updateSteps(steps);
    };

    const atCap = flow.steps.length >= MAX_FLOW_STEPS;
    const selectedIndex = selectedId === "entry" || selectedId === null
        ? -1
        : flow.steps.findIndex((s) => s.id === selectedId);
    const selectedStep = selectedIndex >= 0 ? flow.steps[selectedIndex] : null;

    // #3195: knockout `question_ref` picker. For Form / Questionnaire and
    // Eligibility Check steps we replace the free-text ref with a dropdown of the
    // selected assessment's questions (BE ourteam-backend#3883). Fetch the list for
    // the selected step's assessment; re-fetch when the assessment changes. On any
    // failure the list stays empty and the editor falls back to the textbox.
    const [gateQuestions, setGateQuestions] = useState<AssessmentQuestionOption[]>([]);
    const gateStepType = selectedStep?.type;
    const gateAssessmentId = selectedStep?.assessment_id;
    useEffect(() => {
        if ((gateStepType !== "form" && gateStepType !== "eligibility_check") || !gateAssessmentId) {
            setGateQuestions([]);
            return;
        }
        let alive = true;
        getAssessmentQuestions(gateAssessmentId).then((qs) => { if (alive) setGateQuestions(qs); });
        return () => { alive = false; };
    }, [gateStepType, gateAssessmentId]);

    // Options for the question_ref dropdown. Form / eligibility-with-assessment →
    // the fetched questions; eligibility WITHOUT an assessment → the job's selected
    // eligibility fields (#3195 option b; field key IS the ref).
    const questionRefOptions: { value: string; label: string }[] =
        selectedStep?.type === "eligibility_check" && !selectedStep.assessment_id
            ? ELIGIBILITY_FIELDS.filter((f) => props.workAuthMetadata?.[f.key]).map((f) => ({ value: f.key, label: f.label }))
            : gateQuestions.map((q) => ({ value: q.id, label: q.label || q.id }));
    // The dropdown only replaces the textbox for Form / Eligibility steps that
    // actually have options to show; everything else keeps the free-text ref.
    const showQuestionDropdown =
        (selectedStep?.type === "form" || selectedStep?.type === "eligibility_check") && questionRefOptions.length > 0;
    // #3236: the selected step's conditions as a LIST + list-editing helpers. The
    // question list (#3195) is per-step/assessment, shared by every condition.
    const selectedGates: HiringFlowGate[] = selectedStep ? stepGates(selectedStep) : [];
    const setSelectedGates = (gates: HiringFlowGate[]) => updateStep(selectedIndex, gatesPatch(gates));
    const updateGateAt = (gi: number, gate: HiringFlowGate) => setSelectedGates(selectedGates.map((g, idx) => (idx === gi ? gate : g)));
    const removeGateAt = (gi: number) => setSelectedGates(selectedGates.filter((_, idx) => idx !== gi));
    const newKnockoutGate = (): HiringFlowGate => ({ type: "answer_knockout", question_ref: "", operator: "eq", value: "", on_fail: { action: "flag" } });
    // Per-condition #3195 helpers — a saved ref not in the current assessment is
    // surfaced, never lost.
    const gateRefOf = (g: HiringFlowGate) => (g.type === "answer_knockout" ? g.question_ref : "");
    const gateRefInOptionsFor = (g: HiringFlowGate) => {
        const ref = gateRefOf(g);
        return !!ref && questionRefOptions.some((o) => o.value === ref);
    };
    // #3236: what the condition's VALUE input should be, per the picked question —
    // eligibility fields use the candidate survey's canonical options (salary →
    // number); form questions (#3195/BE #3883) use their MC options, a rating
    // scale's min–max number range, or fall back to free text.
    type GateValueControl =
        | { kind: "options"; options: { value: string; label: string }[] }
        | { kind: "number"; min?: number; max?: number }
        | { kind: "text" };
    const gateValueControlFor = (g: HiringFlowGate): GateValueControl => {
        const ref = gateRefOf(g);
        if (!ref) return { kind: "text" };
        const elig = ELIGIBILITY_VALUE_OPTIONS[ref];
        if (elig === "number") return { kind: "number" };
        if (elig) return { kind: "options", options: elig };
        const q = gateQuestions.find((question) => question.id === ref);
        const opts = q?.options?.filter((o) => o.trim().length > 0) ?? [];
        if (opts.length > 0) return { kind: "options", options: opts.map((o) => ({ value: o, label: o })) };
        if (q && (q.min_rate != null || q.max_rate != null || /rat(e|ing)/i.test(q.type || ""))) {
            return { kind: "number", min: q.min_rate, max: q.max_rate };
        }
        return { kind: "text" };
    };

    // #3257 follow-up: dynamic variables + WhatsApp-style preview for the
    // Custom Message step. The chip inserts the token at the textarea's caret
    // (replacing any selection); the preview substitutes a sample name.
    // whatsapp_first that will actually RUN WhatsApp-first (company WA on) —
    // gates the Custom Message palette entry + the WA flow preview (#3257).
    const waFirstReady = flow.channel === "whatsapp_first" && props.whatsappEnabled !== false;
    const messageTextareaRef = useRef<HTMLTextAreaElement | null>(null);
    const [showMessagePreview, setShowMessagePreview] = useState<boolean>(false);
    // Real chat texts from the BE dry-run (#3996 preview endpoint). null ⇒ the
    // endpoint isn't live / errored — fall back to local step summaries.
    const [flowPreviewMessages, setFlowPreviewMessages] = useState<FlowPreviewMessage[] | null>(null);
    const [flowPreviewWarnings, setFlowPreviewWarnings] = useState<string[]>([]);
    const [flowPreviewLoading, setFlowPreviewLoading] = useState<boolean>(false);
    const openFlowPreview = () => {
        setShowMessagePreview(true);
        setFlowPreviewLoading(true);
        setFlowPreviewMessages(null);
        setFlowPreviewWarnings([]);
        previewFlowChat({
            hiring_flow: flow,
            job_title: props.previewJobTitle,
            company_name: props.previewCompanyName,
            // Eligibility steps take their questions from the job form's live
            // screening ticks (#3257 BE update) — without this the preview only
            // shows the questions the knockout conditions reference.
            work_authorization_metadata: props.workAuthMetadata,
            location_metadata: props.previewLocationMetadata ?? undefined,
            is_enabled_wa_call: props.previewWaCallEnabled,
        })
            .then((res) => {
                setFlowPreviewMessages(res && res.messages.length > 0 ? res.messages : null);
                setFlowPreviewWarnings(res?.warnings ?? []);
            })
            .finally(() => setFlowPreviewLoading(false));
    };
    const insertMessageVariable = (token: string) => {
        if (!selectedStep || selectedStep.type !== "custom_message") return;
        const el = messageTextareaRef.current;
        const current = selectedStep.message || "";
        const start = el?.selectionStart ?? current.length;
        const end = el?.selectionEnd ?? current.length;
        updateStep(selectedIndex, { message: current.slice(0, start) + token + current.slice(end) });
        // Restore focus + caret right after the inserted token.
        requestAnimationFrame(() => {
            if (!el) return;
            el.focus();
            const pos = start + token.length;
            el.setSelectionRange(pos, pos);
        });
    };
    const MESSAGE_VARIABLES = ["{{candidateName}}", "{{jobTitle}}", "{{companyName}}"];
    const substituteMessageVars = (text: string) => text
        .replace(/\{\{candidateName\}\}/g, "Sarah")
        .replace(/\{\{jobTitle\}\}/g, props.previewJobTitle?.trim() || "Senior Software Engineer")
        .replace(/\{\{companyName\}\}/g, props.previewCompanyName?.trim() || "ourteam");

    /** Save-time validation failures for one step — used to mark its node. */
    const stepInvalid = (index: number) => failing.some((c) => !c.warning && c.message.startsWith(`Step ${index + 1} ·`));

    /** One-line config summary shown on a step node. */
    const stepSummary = (step: HiringFlowStep): string => {
        switch (step.type) {
            case "ai_interview": {
                const a = interviewAssessments.find((x) => x.id === step.assessment_id);
                const mode = step.mode === "wa_call" ? "WhatsApp call" : step.mode === "chat" ? "WhatsApp chat" : "Web voice / video";
                return `${a ? a.content.interviewName : "Select an interview"} · ${mode}`;
            }
            case "form": {
                const a = formAssessments.find((x) => x.id === step.assessment_id);
                return a ? a.content.title : "Select a form";
            }
            case "custom_message": {
                const msg = (step.message || "").trim();
                return msg ? `“${msg.length > 42 ? `${msg.slice(0, 42)}…` : msg}”` : "No message yet";
            }
            case "eligibility_check": {
                const n = ELIGIBILITY_FIELDS.filter((f) => props.workAuthMetadata?.[f.key]).length;
                return n > 0 ? `${n} field${n > 1 ? "s" : ""} collected` : "No fields selected — step is skipped";
            }
            case "external_link":
                return (step.url || "").trim() || "Add a URL";
            case "documents": {
                const items = (step.items || []).filter((it) => it.trim());
                if (items.length === 0) return "Add at least 1 document";
                const first = documentLabel(items[0]);
                return items.length > 1 ? `${items.length} documents · ${first} +${items.length - 1}` : first;
            }
            case "human_interview":
                return "Walk-in or online · HR marks it done";
            default:
                return "";
        }
    };

    const suggestJobTitle = (id: string | undefined) =>
        (id && suggestableJobs.find((j) => j.id === id)?.job_title) || undefined;

    const pill = (active: boolean) =>
        cn(
            "px-3 py-1 text-xs font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer",
            active ? "bg-blue-600 text-white" : "text-gray-500 hover:bg-gray-100 hover:text-gray-700",
            disabled && "pointer-events-none opacity-60",
        );

    const nodeCls = (selected: boolean, invalid?: boolean) =>
        cn(
            "w-full max-w-sm mx-auto block rounded-xl border bg-white dark:bg-gray-900 p-3.5 text-left shadow-sm transition-all cursor-pointer",
            selected
                ? "border-blue-500 ring-2 ring-blue-500/20"
                : invalid
                    ? "border-rose-300 hover:border-rose-400 hover:shadow-md"
                    : "border-gray-200 dark:border-gray-700 hover:border-blue-400 hover:shadow-md",
        );

    /** Connector between nodes — a line with the inline "+" (insert at `at`). */
    const connector = (at: number, checkpoint?: boolean) => (
        <div className="flex flex-col items-center">
            <div className="h-3.5 w-px bg-gray-300 dark:bg-gray-600" />
            {checkpoint && (
                <>
                    <span className="inline-flex items-center rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-0.5 text-[10px] font-medium text-gray-500 dark:text-gray-400">
                        Waits for review
                    </span>
                    <div className="h-3.5 w-px bg-gray-300 dark:bg-gray-600" />
                </>
            )}
            <Popover open={addMenuIndex === at} onOpenChange={(open) => setAddMenuIndex(open ? at : null)}>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        disabled={disabled || atCap}
                        title={atCap ? `A flow can have at most ${MAX_FLOW_STEPS} steps.` : "Add a step here"}
                        aria-label="Add a step here"
                        className={cn(
                            "flex h-6 w-6 items-center justify-center rounded-md bg-blue-600 text-white shadow-sm transition-all hover:bg-blue-700 hover:scale-110",
                            (disabled || atCap) && "opacity-40 pointer-events-none",
                        )}
                    >
                        <PlusIcon className="h-3.5 w-3.5" />
                    </button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-2" align="center">
                    <p className="px-2 pb-1.5 pt-1 text-xs font-semibold uppercase tracking-wide text-gray-400">Add a step</p>
                    <div className="space-y-0.5">
                        {CATALOG.map((type) => {
                            const Icon = STEP_TYPE_ICONS[type];
                            // #3257: Custom Message is WhatsApp-first only (see the Build palette).
                            const waOnlyDisabled = type === "custom_message" && !waFirstReady;
                            return (
                                <button
                                    key={type}
                                    type="button"
                                    disabled={waOnlyDisabled}
                                    title={waOnlyDisabled ? "Custom Message needs a WhatsApp-first flow with company WhatsApp enabled — switch the primary channel to WhatsApp-first to use it." : undefined}
                                    onClick={() => addStepAt(type, at)}
                                    className={cn("flex w-full items-start gap-3 rounded-lg p-2 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-800", waOnlyDisabled && "opacity-50 cursor-not-allowed hover:bg-transparent")}
                                >
                                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-950/40">
                                        <Icon className="h-4 w-4 text-blue-600" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{STEP_TYPE_LABELS[type]}</p>
                                        <p className="text-xs text-gray-500">{STEP_TYPE_DESCRIPTIONS[type]}</p>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </PopoverContent>
            </Popover>
            <div className="h-3.5 w-px bg-gray-300 dark:bg-gray-600" />
        </div>
    );

    /** Pass/fail branch drawn under a gated step (display of the step's conditions). */
    const gateBranch = (step: HiringFlowStep) => {
        const gates = stepGates(step);
        if (gates.length === 0) return null;
        // With mixed conditions the knockout branch labels win (any knockout flags).
        const knockouts = gates.filter((g): g is Extract<HiringFlowGate, { type: "answer_knockout" }> => g.type === "answer_knockout");
        const isKnockout = knockouts.length > 0;
        const suggestTitle = isKnockout ? suggestJobTitle(knockouts.find((g) => g.suggest_job_id)?.suggest_job_id) : undefined;
        return (
            <div className="mx-auto w-full max-w-sm">
                {/* stub from the node + splitter across the two lanes */}
                <div className="flex justify-center"><div className="h-3 w-px bg-gray-300 dark:bg-gray-600" /></div>
                <div className="mx-auto h-px w-1/2 bg-gray-300 dark:bg-gray-600" />
                <div className="grid grid-cols-2">
                    <div className="flex flex-col items-center">
                        <div className="h-3 w-px bg-gray-300 dark:bg-gray-600" />
                        <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300">
                            {isKnockout ? "Pass — continue" : "On time — continue"}
                        </span>
                        {/* the row stretches to the taller fail lane — keep the pass line continuous below the pill */}
                        <div className="w-px flex-1 bg-gray-300 dark:bg-gray-600" />
                    </div>
                    <div className="flex flex-col items-center">
                        <div className="h-3 w-px bg-rose-300 dark:bg-rose-800" />
                        <span className="inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[10px] font-medium text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300">
                            {isKnockout ? "Knocked out" : "Deadline missed"}
                        </span>
                        <div className="h-2 w-px bg-rose-300 dark:bg-rose-800" />
                        <div className="max-w-full rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-center text-[11px] font-medium text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
                            {isKnockout ? "Flag & pause for HR" : "Hold for HR (extend / defer)"}
                            {suggestTitle && (
                                <span className="block truncate font-normal text-amber-700 dark:text-amber-400">Suggest: {suggestTitle}</span>
                            )}
                        </div>
                        {/* #3186/#3873: flagged candidates land in the ATS "Flagged" tab. */}
                        <span className="mt-1 max-w-full text-center text-[10px] leading-tight text-gray-400 dark:text-gray-500">
                            Appears in the <span className="font-medium text-amber-700 dark:text-amber-400">Flagged</span> tab for HR to review
                        </span>
                    </div>
                </div>
                {/* the pass lane merges back to the centre line */}
                <div className="grid grid-cols-2">
                    <div className="flex justify-center"><div className="h-3 w-px bg-gray-300 dark:bg-gray-600" /></div>
                    <div />
                </div>
                <div className="ml-[25%] h-px w-1/4 bg-gray-300 dark:bg-gray-600" />
            </div>
        );
    };


    return (
        <div className="space-y-6">
            {/* Template picker (#3071) — hidden while the templates endpoint isn't available */}
            {templatesAvailable && (
                <div className="flex flex-wrap items-center gap-3">
                    <span className="text-xs uppercase tracking-wide text-gray-400 font-semibold">Template</span>
                    <Select
                        value={selectedTemplate?.id || "custom"}
                        onValueChange={(value) => {
                            const tpl = templates.find((t) => t.id === value);
                            if (tpl) applyTemplate(tpl);
                        }}
                        disabled={disabled}
                    >
                        <SelectTrigger className="w-[260px] text-sm flex-shrink-0"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {!selectedTemplate && <SelectItem value="custom">Custom flow</SelectItem>}
                            {templates.map((tpl) => (
                                <SelectItem key={tpl.id} value={tpl.id}>
                                    <span className="inline-flex items-center gap-1.5">
                                        {tpl.is_system && <LockIcon className="w-3 h-3 text-gray-400" />}
                                        {tpl.name}
                                    </span>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {selectedTemplate && !selectedTemplate.is_system && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-red-600" disabled={disabled} onClick={deleteSelectedTemplate} aria-label="Delete template">
                            <Trash2Icon className="w-4 h-4" />
                        </Button>
                    )}
                    <Button variant="outline" size="sm" disabled={disabled} onClick={() => { setTemplateName(""); setTemplateNameError(false); setSaveDialogOpen(true); }}>
                        Save as template
                    </Button>
                </div>
            )}

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
                {/* ============================ Canvas ============================ */}
                <div
                    className="relative rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-950/40 px-4 py-8 [background-image:radial-gradient(circle,#d4d4d8_1px,transparent_1px)] [background-size:16px_16px] dark:[background-image:radial-gradient(circle,#374151_1px,transparent_1px)]"
                    onClick={() => setSelectedId(null)}
                >
                    {/* #3257 follow-up: WhatsApp-style preview of the WHOLE flow —
                        WhatsApp-first flows only (the preview mimics the WA thread). */}
                    {waFirstReady && (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="absolute right-3 top-3 z-10 h-7 gap-1.5 bg-white text-xs shadow-sm dark:bg-gray-900"
                            onClick={(e) => { e.stopPropagation(); openFlowPreview(); }}
                            disabled={flow.steps.length === 0}
                        >
                            <EyeIcon className="h-3.5 w-3.5" />
                            Preview
                        </Button>
                    )}
                    <div className="space-y-0" onClick={(e) => e.stopPropagation()}>
                        {/* Start / entry node — HiringFlow.entry + channel settings */}
                        <button
                            type="button"
                            onClick={() => setSelectedId("entry")}
                            className={nodeCls(selectedId === "entry")}
                        >
                            <div className="flex items-center gap-3">
                                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-950/40">
                                    <PlayIcon className="h-4 w-4 text-blue-600" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Start</p>
                                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">When a candidate applies</p>
                                </div>
                            </div>
                            <div className="mt-2.5 flex flex-wrap gap-1.5">
                                <span className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-[10px] font-medium text-gray-600 dark:text-gray-300">
                                    {flow.channel === "whatsapp_first" ? "WhatsApp-first" : "Web"}
                                </span>
                                {!!flow.chat_native && flow.channel === "whatsapp_first" && (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-300">
                                        <MessageCircleIcon className="h-2.5 w-2.5" />
                                        Maya chat
                                    </span>
                                )}
                                <span className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-[10px] font-medium text-gray-600 dark:text-gray-300">
                                    {flow.entry.auto_start ? "Auto-start" : "HR sends invitation"}
                                </span>
                                <span className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-[10px] font-medium text-gray-600 dark:text-gray-300">
                                    {flow.entry.cv_required_at_apply ? "CV required" : "CV optional"}
                                </span>
                            </div>
                        </button>

                        {connector(0)}

                        {flow.steps.map((step, index) => {
                            const Icon = STEP_TYPE_ICONS[step.type];
                            const isLast = index === flow.steps.length - 1;
                            const invalid = stepInvalid(index);
                            const dupAssessment = (step.type === "form" || step.type === "ai_interview") && !!step.assessment_id && dupAssessmentIds.has(step.assessment_id);
                            return (
                                <div key={step.id}>
                                    <div
                                        draggable={!disabled}
                                        onDragStart={() => setDragIndex(index)}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={() => {
                                            if (dragIndex !== null && dragIndex !== index) moveStep(dragIndex, index);
                                            setDragIndex(null);
                                        }}
                                        onClick={() => setSelectedId(step.id)}
                                        className={cn(nodeCls(selectedId === step.id, invalid), dragIndex === index && "opacity-60 border-blue-400")}
                                    >
                                        <div className="flex items-center gap-3">
                                            <GripVerticalIcon className="h-4 w-4 flex-shrink-0 cursor-grab text-gray-300" />
                                            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-950/40">
                                                <Icon className="h-4 w-4 text-blue-600" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Step {index + 1}</p>
                                                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                    {STEP_TYPE_LABELS[step.type]}
                                                    {step.type === "ai_interview" && <span className="text-red-500 ml-1">*</span>}
                                                </p>
                                                <p className="truncate text-xs text-gray-500">{stepSummary(step)}</p>
                                            </div>
                                            {invalid && <XCircleIcon className="h-4 w-4 flex-shrink-0 text-rose-500" />}
                                        </div>
                                        {stepGates(step).length > 0 && (
                                            <div className="mt-2 flex">
                                                <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
                                                    <FilterIcon className="h-2.5 w-2.5" />
                                                    {/* #3236: show the count when a step carries several conditions. */}
                                                    {stepGates(step).length > 1
                                                        ? `${stepGates(step).length} conditions`
                                                        : stepGates(step)[0].type === "answer_knockout" ? "Knockout condition" : "Deadline"}
                                                </span>
                                            </div>
                                        )}
                                        {dupAssessment && (
                                            <div className="mt-2 flex">
                                                <Tooltip side="top" content={<p className="max-w-xs">This step repeats an assessment already used earlier in the flow.</p>}>
                                                    <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300 cursor-help">
                                                        <AlertTriangleIcon className="h-2.5 w-2.5" />
                                                        Duplicate assessment
                                                    </span>
                                                </Tooltip>
                                            </div>
                                        )}
                                    </div>
                                    {gateBranch(step)}
                                    {connector(index + 1, !isLast && step.advance === "review")}
                                </div>
                            );
                        })}

                        {/* Terminal node (#3253) — the flow's end marker. Labelled END:
                            "Ready for Review" is an ATS stage name, not a diagram terminator. */}
                        <div className="flex justify-center">
                            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 dark:border-emerald-900 dark:bg-emerald-950/40">
                                <FlagIcon className="h-3.5 w-3.5 text-emerald-600" />
                                <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-300">END</span>
                            </div>
                        </div>

                        {atCap && (
                            <p className="mt-3 text-center text-xs text-gray-500">A flow can have at most {MAX_FLOW_STEPS} steps.</p>
                        )}
                    </div>
                </div>

                {/* ========================= Config panel ========================= */}
                <div className="space-y-4 lg:sticky lg:top-4 self-start">
                    {selectedId === "entry" ? (
                        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 space-y-5">
                            <div className="flex items-center gap-3">
                                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-950/40">
                                    <PlayIcon className="h-4 w-4 text-blue-600" />
                                </div>
                                <p className="flex-1 text-sm font-semibold text-gray-900 dark:text-gray-100">Start & channel</p>
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setSelectedId(null)} aria-label="Close panel">
                                    <XIcon className="w-4 h-4" />
                                </Button>
                            </div>
                            {/* Channel + entry settings — job settings, not flow steps (mockup P6) */}
                            <div className="space-y-2">
                                <p className="text-xs uppercase tracking-wide text-gray-400 font-semibold">Primary channel</p>
                                <div className="inline-flex items-center gap-1">
                                    <button type="button" className={pill(flow.channel === "web")} onClick={() => onChange({ ...flow, channel: "web", chat_native: false })}>Web</button>
                                    <button type="button" className={pill(flow.channel === "whatsapp_first")} onClick={() => onChange({ ...flow, channel: "whatsapp_first" })}>WhatsApp-first</button>
                                </div>
                                <p className="text-xs text-gray-500">
                                    {flow.channel === "whatsapp_first"
                                        ? "Candidates go through the flow in a WhatsApp chat — needs company WhatsApp enabled and the candidate's opt-in. Every step still carries a web fallback link."
                                        : "Candidates go through the flow in the browser — invitations and reminders link to a web page for each step."}
                                </p>
                                {/* Chat-native toggle (#3090) — whatsapp_first + company enabled only.
                                    When on, Maya runs the whole flow inside the WhatsApp thread. */}
                                {props.chatNativeEnabled && flow.channel === "whatsapp_first" && (
                                    <div className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 dark:border-gray-700 p-2.5 mt-1">
                                        <Label className="text-xs font-medium flex items-center gap-1.5">
                                            <MessageCircleIcon className="h-3.5 w-3.5 text-green-600" />
                                            Run inside WhatsApp chat (Maya)
                                        </Label>
                                        <Switch
                                            checked={!!flow.chat_native}
                                            onCheckedChange={(checked) => onChange({ ...flow, chat_native: checked })}
                                            disabled={disabled}
                                            className="data-[state=checked]:bg-blue-600"
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="space-y-2">
                                <p className="text-xs uppercase tracking-wide text-gray-400 font-semibold">Entry</p>
                                <div className="inline-flex items-center gap-1">
                                    <button type="button" className={pill(!flow.entry.auto_start)} onClick={() => onChange({ ...flow, entry: { ...flow.entry, auto_start: false } })}>HR sends invitation</button>
                                    <button type="button" className={pill(flow.entry.auto_start)} onClick={() => onChange({ ...flow, entry: { ...flow.entry, auto_start: true } })}>Auto-start</button>
                                </div>
                                <p className="text-xs text-gray-500">
                                    {flow.entry.auto_start
                                        ? "The flow starts the moment the candidate applies — step 1 is sent automatically, no HR action needed."
                                        : "New applicants wait in the Applied tab; the flow only starts after HR sends them the invitation."}
                                </p>
                            </div>
                            <div className="space-y-2">
                                <p className="text-xs uppercase tracking-wide text-gray-400 font-semibold">CV at apply</p>
                                <div className="inline-flex items-center gap-1">
                                    <button type="button" className={pill(flow.entry.cv_required_at_apply)} onClick={() => onChange({ ...flow, entry: { ...flow.entry, cv_required_at_apply: true } })}>Required</button>
                                    <button type="button" className={pill(!flow.entry.cv_required_at_apply)} onClick={() => onChange({ ...flow, entry: { ...flow.entry, cv_required_at_apply: false } })}>Optional</button>
                                </div>
                                <p className="text-xs text-gray-500">
                                    {flow.entry.cv_required_at_apply
                                        ? "Candidates must upload a resume to apply — the profile always has a parsed CV from day one."
                                        : "Candidates can apply without a resume — collect it later in the flow, e.g. with a Document Collection step."}
                                </p>
                            </div>
                        </div>
                    ) : selectedStep ? (
                        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 space-y-5">
                            <div className="flex items-center gap-2">
                                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-950/40">
                                    {(() => { const Icon = STEP_TYPE_ICONS[selectedStep.type]; return <Icon className="h-4 w-4 text-blue-600" />; })()}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Step {selectedIndex + 1}</p>
                                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                                        {STEP_TYPE_LABELS[selectedStep.type]}
                                        {selectedStep.type === "ai_interview" && <span className="text-red-500 ml-1">*</span>}
                                    </p>
                                </div>
                                <div className="flex items-center flex-shrink-0">
                                    <Button variant="ghost" size="icon" className="h-8 w-8" disabled={disabled || selectedIndex === 0} onClick={() => moveStep(selectedIndex, selectedIndex - 1)} aria-label="Move step up">
                                        <ChevronUpIcon className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-8 w-8" disabled={disabled || selectedIndex === flow.steps.length - 1} onClick={() => moveStep(selectedIndex, selectedIndex + 1)} aria-label="Move step down">
                                        <ChevronDownIcon className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-red-600" disabled={disabled} onClick={() => removeStep(selectedIndex)} aria-label="Remove step">
                                        <Trash2Icon className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setSelectedId(null)} aria-label="Close panel">
                                        <XIcon className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                            <p className="text-xs text-gray-500 -mt-3">{STEP_TYPE_DESCRIPTIONS[selectedStep.type]}</p>

                            {/* Per-type config */}
                            {selectedStep.type === "ai_interview" && (
                                <div className="space-y-3">
                                    <Select
                                        value={selectedStep.assessment_id || "None"}
                                        onValueChange={(value) => updateStep(selectedIndex, { assessment_id: value === "None" ? undefined : value })}
                                        disabled={disabled}
                                    >
                                        <SelectTrigger className="w-full"><SelectValue placeholder="Select an interview" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="None">Select an interview…</SelectItem>
                                            {interviewAssessments.map((a) => (
                                                <SelectItem key={a.id} value={a.id || ""}>{a.content.interviewName}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <div className="flex items-center gap-2">
                                        <Button variant="outline" size="sm" className="flex items-center gap-2 text-blue-600 border-blue-200 hover:bg-blue-50" onClick={() => window.open("/assess", "_blank")} disabled={disabled}>
                                            <PlusIcon className="h-4 w-4" />
                                            Create new
                                        </Button>
                                        <Button variant="outline" size="sm" onClick={props.onRefreshAssessments} aria-label="Refresh assessments">
                                            <RefreshCcw className="h-3 w-3" />
                                        </Button>
                                    </div>
                                    {/* Interview delivery mode (#3090): Web / Phone call
                                        (wa_call) / Chat (WhatsApp text via Maya). Chat only
                                        offered when the company has chat-native enabled. */}
                                    {props.mayaEnabled && (
                                        <div className="space-y-1.5 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                                            <Label className="text-sm font-medium flex items-center gap-2">
                                                <PhoneIcon className="h-4 w-4 text-blue-600" />
                                                Interview delivery
                                            </Label>
                                            <Select
                                                value={selectedStep.mode === "wa_call" ? "wa_call" : selectedStep.mode === "chat" ? "chat" : "web"}
                                                onValueChange={(value) =>
                                                    updateStep(selectedIndex,
                                                        // #3248: the web-link fallback is an HR opt-in — a newly
                                                        // selected phone call starts with it OFF (null).
                                                        value === "wa_call" ? { mode: "wa_call", fallback: null }
                                                            : value === "chat" ? { mode: "chat", fallback: "web" }
                                                                : { mode: undefined, fallback: undefined })
                                                }
                                                disabled={disabled}
                                            >
                                                <SelectTrigger className="w-full text-sm"><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="web">Web (voice / video)</SelectItem>
                                                    <SelectItem value="wa_call">Phone call (WhatsApp voice)</SelectItem>
                                                    {props.chatNativeEnabled && (
                                                        <SelectItem value="chat">Chat (WhatsApp text)</SelectItem>
                                                    )}
                                                </SelectContent>
                                            </Select>
                                            {/* #3248 (BE #3990): opt-in web-link fallback when the call
                                                goes unanswered — phone-call mode only. */}
                                            {selectedStep.mode === "wa_call" && (
                                                <div className="mt-2 flex items-start justify-between gap-3 rounded-md bg-gray-50 p-2.5 dark:bg-gray-800/60">
                                                    <div className="min-w-0">
                                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Fall back to the web link</p>
                                                        <p className="text-xs text-gray-500">If the candidate does not answer the call, Maya sends them the web assessment link instead.</p>
                                                    </div>
                                                    <Switch
                                                        checked={selectedStep.fallback === "web"}
                                                        onCheckedChange={(checked) => updateStep(selectedIndex, { fallback: checked ? "web" : null })}
                                                        disabled={disabled}
                                                        className="mt-0.5 flex-shrink-0"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {selectedStep.type === "form" && (
                                <div className="space-y-3">
                                    <Select
                                        value={selectedStep.assessment_id || "None"}
                                        onValueChange={(value) => updateStep(selectedIndex, { assessment_id: value === "None" ? undefined : value })}
                                        disabled={disabled}
                                    >
                                        <SelectTrigger className="w-full"><SelectValue placeholder="Select a form" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="None">Select a form…</SelectItem>
                                            {formAssessments.map((a) => (
                                                <SelectItem key={a.id} value={a.id || ""}>{a.content.title}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <div className="flex items-center gap-2">
                                        <Button variant="outline" size="sm" className="flex items-center gap-2 text-blue-600 border-blue-200 hover:bg-blue-50" onClick={() => window.open("/assess", "_blank")} disabled={disabled}>
                                            <PlusIcon className="h-4 w-4" />
                                            Create new
                                        </Button>
                                        <Button variant="outline" size="sm" onClick={props.onRefreshAssessments} aria-label="Refresh assessments">
                                            <RefreshCcw className="h-3 w-3" />
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {selectedStep.type === "eligibility_check" && (
                                <div className="space-y-2">
                                    <p className="text-xs text-gray-500">Fields to collect — if none are selected, this step is skipped.</p>
                                    <div className="grid grid-cols-1 gap-2">
                                        {ELIGIBILITY_FIELDS.map((field) => (
                                            <div key={field.key} className="flex items-center space-x-2">
                                                <Checkbox
                                                    id={`${selectedStep.id}-${field.key}`}
                                                    checked={props.workAuthMetadata?.[field.key] || false}
                                                    onCheckedChange={(checked) => props.onWorkAuthChange(field.key, checked as boolean)}
                                                    className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 data-[state=checked]:text-white"
                                                    disabled={disabled}
                                                />
                                                <Label htmlFor={`${selectedStep.id}-${field.key}`} className="text-sm font-normal cursor-pointer">
                                                    {field.label}
                                                </Label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {selectedStep.type === "external_link" && (
                                <div className="space-y-3">
                                    <div className="space-y-1">
                                        <Label className="text-sm font-medium">Label</Label>
                                        <Input value={selectedStep.label || ""} placeholder="e.g. Interview details form" onChange={(e) => updateStep(selectedIndex, { label: e.target.value })} disabled={disabled} />
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-sm font-medium">URL <span className="text-red-500">*</span></Label>
                                        <Input type="url" value={selectedStep.url || ""} placeholder="https://…" onChange={(e) => updateStep(selectedIndex, { url: e.target.value })} disabled={disabled} />
                                    </div>
                                    <p className="text-xs text-gray-500">The step completes when the candidate confirms they've finished.</p>
                                </div>
                            )}

                            {selectedStep.type === "documents" && (
                                <div className="space-y-2">
                                    <Label className="text-sm font-medium">Required documents <span className="text-red-500">*</span></Label>
                                    {(selectedStep.items || []).map((item, itemIndex) => {
                                        // Preset when the stored key is a known template key;
                                        // otherwise "Custom…" with the raw value editable
                                        // (back-compat: old free-text keys / seed labels).
                                        const isPreset = DOCUMENT_TEMPLATE.some((d) => d.key === item);
                                        const setItem = (value: string) => {
                                            const items = [...(selectedStep.items || [])];
                                            items[itemIndex] = value;
                                            updateStep(selectedIndex, { items });
                                        };
                                        return (
                                            <div key={itemIndex} className="space-y-1.5">
                                                <div className="flex items-center gap-2">
                                                    <Select
                                                        value={isPreset ? item : DOCUMENT_CUSTOM}
                                                        onValueChange={(value) => setItem(value === DOCUMENT_CUSTOM ? "" : value)}
                                                        disabled={disabled}
                                                    >
                                                        <SelectTrigger className="flex-1 min-w-0 text-sm"><SelectValue placeholder="Select a document…" /></SelectTrigger>
                                                        <SelectContent>
                                                            {DOCUMENT_TEMPLATE.map((d) => (
                                                                <SelectItem key={d.key} value={d.key}>
                                                                    {d.label}
                                                                </SelectItem>
                                                            ))}
                                                            <SelectItem value={DOCUMENT_CUSTOM}>Custom…</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                    <Button
                                                        variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0 text-gray-400 hover:text-red-600"
                                                        disabled={disabled || (selectedStep.items || []).length <= 1}
                                                        onClick={() => updateStep(selectedIndex, { items: (selectedStep.items || []).filter((_, i) => i !== itemIndex) })}
                                                        aria-label="Remove document item"
                                                    >
                                                        <XIcon className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                                {!isPreset && (
                                                    <Input
                                                        value={item}
                                                        placeholder="Custom document key (e.g. reference_letter)"
                                                        onChange={(e) => setItem(e.target.value)}
                                                        disabled={disabled}
                                                        className="w-full"
                                                    />
                                                )}
                                            </div>
                                        );
                                    })}
                                    <Button variant="outline" size="sm" className="text-blue-600 border-blue-200 hover:bg-blue-50" disabled={disabled} onClick={() => {
                                        const used = new Set(selectedStep.items || []);
                                        const next = DOCUMENT_TEMPLATE.find((d) => !used.has(d.key))?.key || "";
                                        updateStep(selectedIndex, { items: [...(selectedStep.items || []), next] });
                                    }}>
                                        <PlusIcon className="w-4 h-4" />
                                        Add document
                                    </Button>
                                </div>
                            )}

                            {selectedStep.type === "human_interview" && (
                                <div className="space-y-1.5">
                                    <p className="rounded-md bg-amber-50 px-2.5 py-1.5 text-xs font-medium text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
                                        Retired step type (#3255) — existing flows keep running, but new Human Interview steps can no longer be added.
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        Starting this step sends the interview invitation (email + WhatsApp) — the candidate waits in Ready for Review until it's marked done.
                                    </p>
                                </div>
                            )}

                            {/* #3257: Custom Message — the whole config is the message text. */}
                            {selectedStep.type === "custom_message" && (
                                <div className="space-y-1.5">
                                    <span className="text-xs text-gray-500">Message to the candidate</span>
                                    <Textarea
                                        ref={messageTextareaRef}
                                        value={selectedStep.message || ""}
                                        onChange={(e) => updateStep(selectedIndex, { message: e.target.value })}
                                        rows={4}
                                        maxLength={MAX_CUSTOM_MESSAGE_LENGTH}
                                        placeholder="Write the WhatsApp message the candidate receives at this step…"
                                        disabled={disabled}
                                    />
                                    {/* Dynamic variables + WhatsApp-style preview (#3257 follow-up). */}
                                    <div className="flex items-start justify-between gap-2">
                                    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
                                        {MESSAGE_VARIABLES.map((token) => (
                                            <button
                                                key={token}
                                                type="button"
                                                onClick={() => insertMessageVariable(token)}
                                                disabled={disabled}
                                                title="Insert at the cursor — replaced with the real value when sent"
                                                className={cn(
                                                    "inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 font-mono text-[11px] font-medium text-blue-700 transition-colors hover:bg-blue-100",
                                                    disabled && "pointer-events-none opacity-60",
                                                )}
                                            >
                                                {token}
                                            </button>
                                        ))}
                                    </div>
                                    {/* BE #3996 caps the message at 1500 chars. */}
                                    <span className={cn(
                                        "flex-shrink-0 text-[11px] tabular-nums",
                                        (selectedStep.message || "").length >= MAX_CUSTOM_MESSAGE_LENGTH ? "font-medium text-rose-600" : "text-gray-400",
                                    )}>
                                        {(selectedStep.message || "").length}/{MAX_CUSTOM_MESSAGE_LENGTH}
                                    </span>
                                    </div>
                                    <p className="text-xs text-gray-500">Sent to the candidate's WhatsApp thread when they reach this step, then the flow continues automatically.</p>
                                </div>
                            )}

                            {/* Conditions (#3236, BE ourteam-backend#3976) — a step can carry
                                several; a candidate is flagged when ANY knockout matches (OR).
                                At most one deadline per step. Flag & pause only.
                                #3257: Custom Message carries no conditions — section hidden. */}
                            {selectedStep.type !== "custom_message" && (
                            <div className="space-y-2">
                                {selectedGates.map((gate, gi) => {
                                    const knockout = gate.type === "answer_knockout" ? gate : null;
                                    const valueControl = gateValueControlFor(gate);
                                    const refInOptions = gateRefInOptionsFor(gate);
                                    const otherHasDeadline = selectedGates.some((g, idx) => idx !== gi && g.type === "deadline");
                                    return (
                                        <div key={gi} className="rounded-lg bg-amber-50 border border-amber-200 p-3 space-y-3">
                                            <div className="flex items-center justify-between">
                                                <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">
                                                    {selectedGates.length > 1 ? `Condition ${gi + 1}` : "Condition"} — flags & pauses, never auto-rejects
                                                </p>
                                                <Button variant="ghost" size="icon" className="h-6 w-6 text-gray-400 hover:text-red-600" disabled={disabled} onClick={() => removeGateAt(gi)} aria-label="Remove condition">
                                                    <XIcon className="w-3.5 h-3.5" />
                                                </Button>
                                            </div>
                                            {knockout && (
                                                <div className="rounded-md bg-amber-100/70 border border-amber-200 px-2.5 py-1.5">
                                                    <p className="text-xs font-semibold text-amber-900">
                                                        Knock out / flag WHEN answer{" "}
                                                        <span className="font-bold">
                                                            {KNOCKOUT_OPERATORS.find((o) => o.value === knockout.operator)?.label ?? knockout.operator}{" "}
                                                            {String(knockout.value ?? "").trim() || "…"}
                                                        </span>
                                                    </p>
                                                    <p className="mt-0.5 text-[11px] text-amber-800">
                                                        Candidates whose{" "}
                                                        <span className="font-medium">{knockout.question_ref?.trim() || "answer"}</span>{" "}
                                                        {KNOCKOUT_OPERATORS.find((o) => o.value === knockout.operator)?.label ?? knockout.operator}{" "}
                                                        <span className="font-medium">{String(knockout.value ?? "").trim() || "…"}</span>{" "}
                                                        will be flagged. This is the disqualifying condition, not a requirement.
                                                    </p>
                                                </div>
                                            )}
                                            <div className="space-y-2">
                                                <Select
                                                    value={gate.type}
                                                    onValueChange={(value) =>
                                                        updateGateAt(gi, value === "deadline"
                                                            ? { type: "deadline", due: "same_day_17:00", reminders: ["-4h", "-1h"], on_miss: { action: "hold", auto_reject: false } }
                                                            : newKnockoutGate())
                                                    }
                                                    disabled={disabled}
                                                >
                                                    <SelectTrigger className="w-full text-sm bg-white dark:bg-gray-900"><SelectValue /></SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="answer_knockout">Answer knockout</SelectItem>
                                                        {/* ≤1 deadline per step (#3236). */}
                                                        <SelectItem value="deadline" disabled={otherHasDeadline}>Deadline</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                                {knockout ? (
                                                    <>
                                                        {/* #3195: question picker for Form / Eligibility steps (BE #3883);
                                                            other step types (or before the endpoint is live) keep the free-text ref. */}
                                                        {showQuestionDropdown ? (
                                                            <>
                                                                <Select
                                                                    value={knockout.question_ref || undefined}
                                                                    onValueChange={(value) => updateGateAt(gi, { ...knockout, question_ref: value })}
                                                                    disabled={disabled}
                                                                >
                                                                    <SelectTrigger className="w-full text-sm bg-white dark:bg-gray-900"><SelectValue placeholder="Select a question…" /></SelectTrigger>
                                                                    <SelectContent>
                                                                        {/* Keep a previously-saved ref visible even if it isn't in
                                                                            the current assessment's questions, so it's flagged, not lost. */}
                                                                        {knockout.question_ref && !refInOptions && (
                                                                            <SelectItem value={knockout.question_ref}>{knockout.question_ref} (not in this assessment)</SelectItem>
                                                                        )}
                                                                        {questionRefOptions.map((o) => (
                                                                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                                                        ))}
                                                                    </SelectContent>
                                                                </Select>
                                                                {knockout.question_ref && !refInOptions && (
                                                                    <p className="text-xs text-amber-700">This question isn't in the selected assessment — pick one from the list.</p>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <Input className="w-full" placeholder="Question ref (e.g. q_education)" value={knockout.question_ref} onChange={(e) => updateGateAt(gi, { ...knockout, question_ref: e.target.value })} disabled={disabled} />
                                                        )}
                                                        <Select
                                                            value={knockout.operator}
                                                            onValueChange={(value) => updateGateAt(gi, { ...knockout, operator: value as KnockoutOperator })}
                                                            disabled={disabled}
                                                        >
                                                            <SelectTrigger className="w-full text-sm bg-white dark:bg-gray-900"><SelectValue /></SelectTrigger>
                                                            <SelectContent>
                                                                {KNOCKOUT_OPERATORS.map((op) => (
                                                                    <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                        {/* #3195/#3236: the VALUE input follows the picked question —
                                                            eligibility fields / MC questions offer their answer options,
                                                            rating scales a bounded number, anything else free text. */}
                                                        {valueControl.kind === "options" ? (
                                                            <Select
                                                                value={String(knockout.value ?? "") || undefined}
                                                                onValueChange={(value) => updateGateAt(gi, { ...knockout, value })}
                                                                disabled={disabled}
                                                            >
                                                                <SelectTrigger className="w-full text-sm bg-white dark:bg-gray-900"><SelectValue placeholder="Select a value…" /></SelectTrigger>
                                                                <SelectContent>
                                                                    {/* Keep a saved value visible even when it isn't an option. */}
                                                                    {knockout.value && !valueControl.options.some((o) => o.value === String(knockout.value)) && (
                                                                        <SelectItem value={String(knockout.value)}>{String(knockout.value)}</SelectItem>
                                                                    )}
                                                                    {valueControl.options.map((opt) => (
                                                                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                        ) : valueControl.kind === "number" ? (
                                                            <Input
                                                                type="number"
                                                                className="w-full"
                                                                min={valueControl.min}
                                                                max={valueControl.max}
                                                                placeholder={valueControl.min != null || valueControl.max != null
                                                                    ? `Value (${valueControl.min ?? "…"}–${valueControl.max ?? "…"})`
                                                                    : "Value (number)"}
                                                                value={String(knockout.value ?? "")}
                                                                onChange={(e) => updateGateAt(gi, { ...knockout, value: e.target.value })}
                                                                disabled={disabled}
                                                            />
                                                        ) : (
                                                            <Input className="w-full" placeholder="Value (e.g. diploma)" value={String(knockout.value ?? "")} onChange={(e) => updateGateAt(gi, { ...knockout, value: e.target.value })} disabled={disabled} />
                                                        )}
                                                        {/* Reroute target (#3086) — optional; HR resolving the flag
                                                            gets a one-click "Reroute to {job}" action. */}
                                                        <Select
                                                            value={knockout.suggest_job_id || "none"}
                                                            onValueChange={(value) => updateGateAt(gi, { ...knockout, suggest_job_id: value === "none" ? undefined : value })}
                                                            disabled={disabled}
                                                        >
                                                            <SelectTrigger className="w-full text-sm bg-white dark:bg-gray-900"><SelectValue placeholder="Suggest another job…" /></SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value="none">No suggested job</SelectItem>
                                                                {/* Keep a loaded flow's selection visible even when the
                                                                    job isn't in the fetched page (unpublished / >100). */}
                                                                {knockout.suggest_job_id && !suggestableJobs.some((j) => j.id === knockout.suggest_job_id) && (
                                                                    <SelectItem value={knockout.suggest_job_id}>Current selection</SelectItem>
                                                                )}
                                                                {suggestableJobs.map((j) => (
                                                                    <SelectItem key={j.id} value={j.id}>{j.job_title}</SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </>
                                                ) : gate.type === "deadline" ? (
                                                    <DeadlineGateFields
                                                        gate={gate}
                                                        disabled={disabled}
                                                        onChange={(g) => updateGateAt(gi, g)}
                                                    />
                                                ) : null}
                                            </div>
                                            <p className="text-xs text-amber-700">
                                                {knockout ? (
                                                    <>
                                                        On fail the candidate is flagged (Knockout Flagged) and paused for HR to keep or archive.{" "}
                                                        To require e.g. &quot;Diploma or higher&quot;, flag those{" "}
                                                        <span className="font-semibold">below</span> it: operator{" "}
                                                        <span className="font-semibold">&quot;is below&quot;</span>, value{" "}
                                                        <span className="font-semibold">Diploma</span>.
                                                    </>
                                                ) : (
                                                    "On miss the candidate is held (Deadline Missed) for HR to extend, defer or archive."
                                                )}
                                            </p>
                                        </div>
                                    );
                                })}
                                {selectedGates.length > 1 && (
                                    <p className="text-xs text-gray-500">
                                        Candidates are flagged when <span className="font-semibold">any</span> condition matches.
                                    </p>
                                )}
                                {/* AI Interview steps carry no gateable answers (knockout
                                    conditions reference Form/Questionnaire + Eligibility
                                    questions, #3195) — hide the add button. A gate already
                                    saved on such a step still renders above for removal. */}
                                {selectedStep.type !== "ai_interview" && (
                                    <button
                                        type="button"
                                        className={cn("inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-800", disabled && "pointer-events-none opacity-60")}
                                        onClick={() => setSelectedGates([...selectedGates, newKnockoutGate()])}
                                    >
                                        <FilterIcon className="w-3.5 h-3.5" />
                                        + Add condition
                                    </button>
                                )}
                            </div>
                            )}

                            {/* Advance — the final step always ends in Ready for Review */}
                            {/* #3257: hidden for Custom Message — fire-and-continue; the
                                textarea hint already explains it. */}
                            {selectedStep.type !== "custom_message" && (
                            <div className="space-y-1.5">
                                <span className="text-xs text-gray-500">After this step</span>
                                {selectedIndex === flow.steps.length - 1 ? (
                                    <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Ends in Ready for Review</p>
                                ) : (
                                    <Select
                                        value={selectedStep.advance || "auto"}
                                        onValueChange={(value) => updateStep(selectedIndex, { advance: value as HiringFlowStep["advance"] })}
                                        disabled={disabled}
                                    >
                                        <SelectTrigger className="w-full h-8 text-sm"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="auto">Continue automatically</SelectItem>
                                            <SelectItem value="review">Wait for review (checkpoint)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                )}
                            </div>
                            )}
                        </div>
                    ) : (
                        /* Nothing selected — build palette (append at the end) */
                        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 space-y-3">
                            <div>
                                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Build</p>
                                <p className="text-xs text-gray-500">Click a block on the canvas to configure it, or add a step below.</p>
                            </div>
                            <div className="space-y-1">
                                {CATALOG.map((type) => {
                                    const Icon = STEP_TYPE_ICONS[type];
                                    // #3257: Custom Message exists only on WhatsApp-first flows —
                                    // disabled elsewhere, with an explanatory hover popover.
                                    const waOnlyDisabled = type === "custom_message" && !waFirstReady;
                                    const card = (
                                        <button
                                            type="button"
                                            onClick={() => addStepAt(type, flow.steps.length)}
                                            disabled={disabled || atCap || waOnlyDisabled}
                                            className={cn(
                                                "flex w-full items-start gap-3 rounded-lg border border-gray-200 dark:border-gray-700 p-2.5 text-left transition-all",
                                                "hover:border-blue-500 hover:shadow-sm",
                                                (disabled || atCap) && "opacity-50 pointer-events-none",
                                                waOnlyDisabled && "opacity-50 cursor-not-allowed hover:border-gray-200 hover:shadow-none",
                                            )}
                                        >
                                            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-950/40">
                                                <Icon className="h-4 w-4 text-blue-600" />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{STEP_TYPE_LABELS[type]}</p>
                                                <p className="text-xs text-gray-500">{STEP_TYPE_DESCRIPTIONS[type]}</p>
                                            </div>
                                        </button>
                                    );
                                    return waOnlyDisabled ? (
                                        <Tooltip key={type} side="left" content={<p className="max-w-xs">Custom Message needs a WhatsApp-first flow with company WhatsApp enabled — switch the primary channel to WhatsApp-first to use it.</p>}>
                                            <span className="block cursor-not-allowed">{card}</span>
                                        </Tooltip>
                                    ) : (
                                        <span key={type} className="block">{card}</span>
                                    );
                                })}
                            </div>
                            {atCap && (
                                <p className="text-xs text-gray-500">A flow can have at most {MAX_FLOW_STEPS} steps.</p>
                            )}
                        </div>
                    )}

                    {/* Flow check */}
                    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 space-y-2">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Flow check</p>
                        <ul className="space-y-1.5">
                            {(failing.length > 0 ? failing : [{ ok: true, message: "Flow is valid — candidates complete these steps in order.", warning: false }]).map((item, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm">
                                    {item.ok ? (
                                        <CheckCircle2Icon className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                                    ) : item.warning ? (
                                        <AlertTriangleIcon className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                                    ) : (
                                        <XCircleIcon className="w-4 h-4 text-rose-600 flex-shrink-0 mt-0.5" />
                                    )}
                                    <span className={item.ok ? "text-gray-700 dark:text-gray-300" : item.warning ? "text-amber-700 dark:text-amber-400" : "text-rose-700"}>{item.message}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>

            {/* Save-as-template dialog (#3071) — name required; system templates
                are never overwritten (saving always creates a company copy). */}
            {/* #3257 follow-up: WhatsApp-style preview of the Custom Message. */}
            <Dialog open={showMessagePreview} onOpenChange={(open) => { if (!open) setShowMessagePreview(false); }}>
                <DialogContent className="sm:max-w-md overflow-hidden p-0 gap-0 border-0 outline-none focus:outline-none focus-visible:outline-none">
                    {/* WA chat header */}
                    <div className="flex items-center gap-3 bg-[#075E54] px-4 py-3 text-white">
                        <img
                            src={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/ourteam_logo_only.png`}
                            alt="Maya"
                            className="h-8 w-8 rounded-full bg-white object-cover"
                        />
                        <div className="min-w-0">
                            <p className="text-sm font-semibold leading-tight">Maya</p>
                            <p className="text-[11px] leading-tight text-white/70">WhatsApp Business</p>
                        </div>
                    </div>
                    {/* Chat area — the WHOLE flow as incoming bubbles, WhatsApp look */}
                    <div className="max-h-[60vh] min-h-[220px] space-y-3 overflow-y-auto bg-[#ECE5DD] px-4 py-6 dark:bg-[#0B141A]">
                        <div className="flex justify-center">
                            <span className="rounded-md bg-white/90 px-2 py-0.5 text-[10px] text-gray-500 shadow-sm dark:bg-[#202C33] dark:text-gray-300">Candidate applies — flow starts</span>
                        </div>
                        {flowPreviewLoading ? (
                            <div className="flex justify-center py-10">
                                <Loader2Icon className="h-5 w-5 animate-spin text-gray-400" />
                            </div>
                        ) : flowPreviewMessages ? (
                            /* Real texts from the BE dry-run — exactly what Maya sends. */
                            flowPreviewMessages.map((m, i) => (
                                <div key={`${m.step_id || "sys"}-${i}`} className="relative max-w-[85%] rounded-lg rounded-tl-none bg-white px-3 py-2 shadow-sm dark:bg-[#202C33]">
                                    <p className="whitespace-pre-wrap break-words text-sm text-gray-800 dark:text-gray-100">{m.text}</p>
                                    <p className="mt-1 text-right text-[10px] text-gray-400">09:41</p>
                                </div>
                            ))
                        ) : (
                            /* Fallback until BE #3996's preview endpoint is live: step
                               labels/summaries (custom messages show their real text). */
                            flow.steps.map((step, i) => (
                                <div key={step.id} className="relative max-w-[85%] rounded-lg rounded-tl-none bg-white px-3 py-2 shadow-sm dark:bg-[#202C33]">
                                    <p className="text-[10px] font-semibold uppercase tracking-wide text-[#075E54] dark:text-emerald-400">Step {i + 1} · {STEP_TYPE_LABELS[step.type]}</p>
                                    <p className="mt-0.5 whitespace-pre-wrap break-words text-sm text-gray-800 dark:text-gray-100">
                                        {step.type === "custom_message"
                                            ? (substituteMessageVars(step.message || "").trim() || "(no message yet)")
                                            : stepSummary(step)}
                                    </p>
                                    <p className="mt-1 text-right text-[10px] text-gray-400">09:41</p>
                                </div>
                            ))
                        )}
                        <div className="flex justify-center">
                            <span className="rounded-md bg-white/90 px-2 py-0.5 text-[10px] text-gray-500 shadow-sm dark:bg-[#202C33] dark:text-gray-300">Flow complete — Ready for Review</span>
                        </div>
                    </div>
                    {flowPreviewWarnings.length > 0 && (
                        <div className="space-y-0.5 border-t border-amber-200 bg-amber-50 px-4 py-2 dark:border-amber-900 dark:bg-amber-950/40">
                            {flowPreviewWarnings.map((w, wi) => (
                                <p key={wi} className="text-[11px] text-amber-700 dark:text-amber-300">{w}</p>
                            ))}
                        </div>
                    )}
                    <div className="border-t border-gray-200 bg-white px-4 py-2.5 dark:border-gray-800 dark:bg-gray-900">
                        <p className="text-[11px] text-gray-500">
                            {flowPreviewMessages
                                ? "Preview — the exact messages the candidate receives on WhatsApp (sample candidate name)."
                                : flow.steps.some((st) => st.type === "custom_message")
                                    ? <>Approximate preview — variables (<span className="font-mono">{"{{candidateName}}"}</span>, <span className="font-mono">{"{{jobTitle}}"}</span>, <span className="font-mono">{"{{companyName}}"}</span>) are replaced with the real values when sent.</>
                                    : "Approximate preview — how the candidate moves through this flow on WhatsApp."}
                        </p>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={isSaveDialogOpen} onOpenChange={setSaveDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Save as template</DialogTitle>
                        <DialogDescription>
                            Company templates are available on every future job in this workspace. System templates cannot be overwritten.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        <Label htmlFor="hiring-flow-template-name" className="text-sm font-medium">
                            Template name <span className="text-red-500">*</span>
                        </Label>
                        <Input
                            id="hiring-flow-template-name"
                            value={templateName}
                            placeholder="e.g. Retail WhatsApp screening"
                            onChange={(e) => {
                                setTemplateName(e.target.value);
                                if (e.target.value.trim()) setTemplateNameError(false);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") saveTemplate();
                            }}
                        />
                        {templateNameError && (
                            <p className="text-sm text-red-500">Please enter a template name.</p>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
                        <Button onClick={saveTemplate} disabled={isSavingTemplate}>
                            {isSavingTemplate ? "Saving…" : "Save template"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
